import React, { useState, useEffect } from 'react';

/** validation de formulaire */
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import usePostAxiosData from 'hooks/usePostAxiosData';
import toast from 'react-hot-toast';
import InputField from 'components/fields/InputField';
import Error from 'components/message/Error';
import { config } from 'variables/config';
import useDataFetching from 'hooks/useDataFetching';
import MuiSelect from 'components/MuiSelect';
import MuiDatePicker from 'components/MuiDatePicker';
import { AppBar, Dialog, DialogContent, Toolbar, Typography } from '@mui/material';
import MButton from 'components/Mbutton';
import { CloseModalButton } from 'components/Utils';
import MultipleSelect from 'components/MultipleSelect';
import { useTranslation } from 'react-i18next'; // Importer useTranslation

const AddTask = (props) => {
  const canEdit = true;
  const { isOpen, onClose, selectedTask } = props;
  const { t } = useTranslation('add_task');

  /** Form validation config */
  const schema = yup.object().shape({
    description: yup.string(),
    observations: yup.string(),
  });

  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [regionId, setRegionId] = useState(null);
  const [departmentId, setDepartmentsId] = useState(null);
  const [arrondissementId, setArrId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [zoneId, setZoneId] = useState(null);

  const [loadingZ, errorZ, zones, fetchDataZ] = useDataFetching(`${config.app.api_url}/zones`);

  const [loadingR, errorR, regions, fetchDataR] = useDataFetching(`${config.app.api_url}/regions`);
  const [loadingU, errorU, users, fetchDataU] = useDataFetching(
    `${config.app.api_url}/roles/agents`
  );

  const [loadingD, errorD, departments, fetchDataD] = useDataFetching(
    regionId && regionId !== '*' ? `${config.app.api_url}/regions/${regionId}/departments` : null
  );

  const [loadingA, errorA, arrondissements, fetchDataA] = useDataFetching(
    departmentId && departmentId !== '*'
      ? `${config.app.api_url}/departments/${departmentId}/arrondissements`
      : null
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  console.log('🚀 ~ AddTask ~ errors:', errors);

  const onSubmitHandler = (data) => {
    handleSave(data);
  };

  /** post/put create/update user */
  const [loading, postAxiosData, errorMassage, successMessage, result] = usePostAxiosData(
    `tasks`,
    selectedTask ? 'PUT' : 'POST'
  ); // Change method based on selectedTask

  const handleSave = async (dataYub) => {
    const data = {
      id: selectedTask?.id,
      ...dataYub,
      regionId,
      departmentId,
      arrondissementId,
      zoneId,
      start,
      end,
      participants,
    };
    console.log('🚀 ~ handleSave ~ data:', data);
    await postAxiosData(data);
  };

  const handleClose = () => {
    setArrId(null);
    setRegionId(null);
    setDepartmentsId(null);
    setStart(null);
    setEnd(null);
    setParticipants([]);
    setZoneId(null);
    reset();
    onClose();
  };

  /** gestion des erreurs et succès */
  useEffect(() => {
    if (successMessage?.status === 200 || successMessage?.status === 201) {
      reset();
      toast.success(
        `${
          successMessage?.message != null
            ? successMessage?.message
            : selectedTask
              ? t('task_updated_success')
              : t('task_added_success') // Traduit
        } `,
        {
          duration: 4000,
          className: 'bg-success text-white',
        }
      );
      props?.endFetch();
      handleClose();
    }
  }, [successMessage, reset, props, handleClose, selectedTask, t]);

  useEffect(() => {
    if (selectedTask) {
      setStart(new Date(selectedTask?.start));
      setEnd(new Date(selectedTask?.end));
      setParticipants(selectedTask?.participants?.map((p) => p?.id));
      setRegionId(selectedTask?.region?.id);
      setDepartmentsId(selectedTask?.department?.id);
      setArrId(selectedTask?.arrondissement?.id);
      setZoneId(selectedTask?.zone?.id);
      setValue('description', selectedTask?.description);
      setValue('observations', selectedTask?.observations);
    }
  }, [selectedTask, setValue]);

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={'md'}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar className="bg-gray-50">
            <Typography
              sx={{ ml: 2, flex: 1 }}
              color={'green'}
              fontWeight={'bold'}
              variant="h6"
              component="div"
            >
              {selectedTask ? t('edit_task_title') : t('add_task_title')} {/* Traduit */}
            </Typography>
            <CloseModalButton iconColor={'red'} handleClose={handleClose} />
          </Toolbar>
        </AppBar>

        <DialogContent dividers>
          <form onSubmit={handleSubmit(onSubmitHandler)}>
            <div className="my-4 grid gap-2 md:grid-cols-1">
              {errorMassage?.message && (
                <Error message={errorMassage?.message !== '' && errorMassage?.message} />
              )}

              <MultipleSelect
                label={t('select_participants_label')}
                value={participants}
                options={users.data?.map((u) => u[0])}
                format={{ value: 'id', label: 'firstName' }}
                onChange={(event) => {
                  setParticipants(event.target.value);
                }}
                emptyLabel={t('none_option')}
              />

              <MuiSelect
                label={t('region_label')}
                value={regionId}
                options={regions.data}
                format={{ value: 'id', label: 'name' }}
                onChange={(event) => {
                  setRegionId(event.target.value);
                }}
                emptyLabel={t('none_option')}
              />

              <MuiSelect
                label={t('department_label')}
                value={departmentId}
                options={departments.data}
                format={{ value: 'id', label: 'name' }}
                onChange={(event) => {
                  setDepartmentsId(event.target.value);
                }}
                emptyLabel={t('none_option')}
              />
              <MuiSelect
                label={t('arrondissement_label')}
                value={arrondissementId}
                options={arrondissements.data}
                format={{ value: 'id', label: 'name' }}
                onChange={(event) => {
                  setArrId(event.target.value);
                }}
                emptyLabel={t('none_option')}
              />
              <MuiSelect
                label={t('zone_label')}
                value={zoneId}
                options={zones.data}
                format={{ value: 'id', label: 'description' }}
                onChange={(event) => {
                  setZoneId(event.target.value);
                }}
                emptyLabel={t('none_option')}
              />
              <InputField
                register={register}
                variant="auth"
                extra="mb-3"
                label={t('description_label')}
                required={true}
                placeholder={t('description_placeholder')}
                id="description"
                name="description"
                type="text"
                error={errors.description?.message !== '' ? errors.description?.message : ''}
              />
              <div className="flex gap-2">
                <MuiDatePicker
                  label={t('start_date_label')}
                  value={start}
                  onChange={(newValue) => {
                    setStart(new Date(newValue));
                  }}
                  wrapperClasses={'flex-1 filter-item anime slideLeft'}
                />
                <MuiDatePicker
                  label={t('end_date_label')}
                  value={end}
                  onChange={(newValue) => {
                    setEnd(new Date(newValue));
                  }}
                  wrapperClasses={'flex-1 filter-item anime slideLeft'}
                />
              </div>

              <InputField
                register={register}
                variant="auth"
                extra="mb-3"
                label={t('observations_label')}
                required={true}
                placeholder={t('observations_placeholder')}
                id="observations"
                name="observations"
                type="text"
                multiline={true}
                error={errors.observations?.message !== '' ? errors.observations?.message : ''}
              />
            </div>
            <div className="flex justify-between p-4">
              <div>
                <MButton
                  variant={'danger'}
                  onClick={(e) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    onClose();
                  }}
                >
                  {t('close_button')} {/* Traduit */}
                </MButton>
              </div>
              <div>
                <MButton isLoading={loading} variant={'primary'} type="submit" colorScheme="green">
                  {selectedTask ? t('edit_button') : t('add_button')} {/* Traduit */}
                </MButton>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddTask;
